// A child's places, folded from the log (pure): what they want to go to (kit ideas or their own
// from a link) and what they've found by checking in. Plus the one-off "am I near?" check a
// check-in makes on the phone; the position itself is never kept (shared/places.ts).
import { Checkin, PlaceWanted, type OwnPlace } from '../../shared/places'
import type { DayItem, Pack, Place } from '../../shared/pack/schema'

interface LogEvent {
  id: string
  type: string
  at: number
  personId: string
  payload: unknown
}

export interface MyPlaces {
  /** Want-to-go place ids, oldest first, with the place itself when it came from a link. */
  wanted: Map<string, OwnPlace | null>
  /** Places checked in at (near: yes). */
  found: Set<string>
}

export function myPlaces(events: readonly LogEvent[], personId: string): MyPlaces {
  const wanted = new Map<string, OwnPlace | null>()
  const found = new Set<string>()
  for (const e of [...events].sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1))) {
    if (e.personId !== personId) continue
    if (e.type === 'place.wanted') {
      const p = PlaceWanted.safeParse(e.payload)
      if (p.success) wanted.set(p.data.placeId, p.data.place ?? null)
    } else if (e.type === 'place.unwanted') {
      wanted.delete((e.payload as { placeId?: string } | null)?.placeId ?? '')
    } else if (e.type === 'checkin') {
      const p = Checkin.safeParse(e.payload)
      if (p.success && p.data.near) found.add(p.data.placeId)
    }
  }
  return { wanted, found }
}

/**
 * What's new in the Crate since this person last opened it on this phone (the dock's Crate
 * badge; owner, 23 Oct: "34 of what?" — it used to count the trip's ideas). The Crate fills with
 * the places found by checking in, so "new" is the trip's found places not in `seen` (the ids
 * found when the Crate was last open; nothing seen yet: every find is new).
 */
export function crateNew(found: ReadonlySet<string>, tripPlaceIds: readonly string[], seen: readonly string[]): string[] {
  const was = new Set(seen)
  return tripPlaceIds.filter((id) => found.has(id) && !was.has(id))
}

/** Metres between two [lat, lng] points (haversine). */
export function distanceM(a: readonly [number, number], b: readonly [number, number]): number {
  const r = (d: number) => (d * Math.PI) / 180
  const dLat = r(b[0] - a[0])
  const dLng = r(b[1] - a[1])
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a[0])) * Math.cos(r(b[0])) * Math.sin(dLng / 2) ** 2
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Plans today that can be checked in at: they name a place the child can see. */
export function checkinTargets(pack: Pick<Pack, 'itinerary' | 'places'>, date: string): { item: DayItem; place: Place }[] {
  return pack.itinerary.dayItems.flatMap((item) => {
    if (item.date !== date || !item.placeId) return []
    const place = pack.places.find((p) => p.id === item.placeId)
    return place ? [{ item, place }] : []
  })
}

/** Whether a check-in counts: the phone is within `radiusM` of the place. */
export function isNear(pos: readonly [number, number], place: Pick<Place, 'at'>, radiusM: number): boolean {
  return distanceM(pos, place.at) <= radiusM
}
